import { useEffect, useState } from "react";
import { api, useAuth } from "../../App";
import { mono, Chip, Btn, Select, Input, Textarea, Field, PageTitle, Drawer } from "./ptUi";

// Shoots — the Shoot Pipeline the production crew fills (mirrors the Excel master
// tracker): studio, crew, 8-point readiness checklist, call/wrap, delivery,
// handover. Writes pt_shoots in DM's Mongo. Feeds today's shoots on My Day.

const CHECKLIST = [
  ["call_sheet", "Call sheet"], ["crew", "Crew"], ["equipment", "Equipment"], ["props", "Props"],
  ["styling", "Styling"], ["set_art", "Set / Art"], ["talent", "Talent"], ["backup_plan", "Backup plan"],
];
const STATUSES = ["Planned", "Ready", "In Progress", "Wrapped", "Delivered", "Cancelled"];
const TYPES = ["Product Film", "Social Content", "Photoshoot", "Campaign Film", "Test / Recce", "Other"];

function ReadinessBar({ pct, done, status }) {
  const c = status === "Ready" ? "#0ca30c" : status === "Nearly" ? "#d97706" : "var(--r)";
  return (
    <div style={{ width: 90 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: mono, fontSize: 9, color: "var(--t3)", marginBottom: 3 }}><span>{done}/8</span><span>{status}</span></div>
      <div style={{ height: 6, background: "var(--s2)", borderRadius: 4, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: c, borderRadius: 4 }} /></div>
    </div>
  );
}

export default function PtrackerShoots() {
  const { user } = useAuth();
  const isLead = ["admin", "srmanager", "manager", "creativedirector", "associatecreativedirector"].includes((user?.role || "").toLowerCase());
  const [range, setRange] = useState("upcoming");
  const [shoots, setShoots] = useState([]);
  const [members, setMembers] = useState([]);
  const [lists, setLists] = useState({ brands: [], studios: [] });
  const [drawer, setDrawer] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get(`/ptracker/shoots?range=${range}`).then((r) => setShoots(r.data.shoots || [])).catch((e) => setErr(e.response?.data?.detail || "Couldn't load shoots."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);
  useEffect(() => { api.get("/ptracker/refs/members").then((r) => setMembers(r.data.members || [])).catch(() => {}); api.get("/ptracker/refs/lists").then((r) => setLists(r.data)).catch(() => {}); }, []);

  const th = { textAlign: "left", fontFamily: mono, fontSize: 10, fontWeight: 700, color: "var(--t3)", letterSpacing: ".08em", textTransform: "uppercase", padding: "8px 10px" };
  const td = { padding: "8px 10px", fontSize: 13, color: "var(--t1)", verticalAlign: "middle" };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1300, margin: "0 auto" }} data-testid="ptracker-shoots">
      <PageTitle title="Shoots" sub="The shoot pipeline — studio, crew, readiness, call/wrap, delivery & handover."
        right={isLead ? <Btn onClick={() => setDrawer({ shoot_date: new Date().toISOString().slice(0, 10), studio_location: lists.studios[0] || "", shoot_status: "Planned", planned_call: "09:00", planned_wrap: "18:00", checklist: {} })}>+ Add shoot</Btn> : null} />

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <Select value={range} onChange={(e) => setRange(e.target.value)} options={[{ value: "upcoming", label: "Upcoming" }, { value: "past", label: "Past" }, { value: "all", label: "All" }]} />
      </div>
      {err && <div style={{ color: "var(--r)", fontFamily: mono, fontSize: 12, marginBottom: 10 }}>{err}</div>}

      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--s2)" }}>
            <tr><th style={th}>Date</th><th style={th}>Brand</th><th style={th}>Studio</th><th style={th}>Type</th><th style={th}>Director</th><th style={th}>Readiness</th><th style={th}>Call–Wrap</th><th style={th}>Status</th><th style={th}>Handover</th></tr>
          </thead>
          <tbody>
            {shoots.map((s) => (
              <tr key={s.id} style={{ borderTop: "1px solid var(--bdr)", cursor: isLead ? "pointer" : "default" }} onClick={() => isLead && setDrawer(s)}>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t3)", whiteSpace: "nowrap" }}>{s.shoot_date}</td>
                <td style={{ ...td, fontWeight: 600 }}>{s.brand_project}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{s.studio_location}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{s.type || "—"}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{s.director_name || "—"}</td>
                <td style={td}><ReadinessBar pct={s.readiness_pct} done={s.readiness_done} status={s.readiness_status} /></td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t2)", whiteSpace: "nowrap" }}>{(s.actual_call || s.planned_call || "—")} – {(s.actual_wrap || s.planned_wrap || "—")}</td>
                <td style={td} onClick={(e) => e.stopPropagation()}><Chip value={s.shoot_status} /></td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11 }}>
                  {s.handover_date ? <span style={{ color: "var(--t2)" }}>{s.handover_date}{s.same_day_handover && <span style={{ marginLeft: 4, color: "#0ca30c" }}>·same-day</span>}</span>
                    : ["Wrapped", "Delivered"].includes(s.shoot_status) ? <span style={{ color: "#d97706" }}>Pending</span> : "—"}
                </td>
              </tr>
            ))}
            {shoots.length === 0 && <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", fontFamily: mono, fontSize: 13, color: "var(--t3)" }}>No shoots in this view.</td></tr>}
          </tbody>
        </table>
      </div>

      {drawer && <ShootForm shoot={drawer} members={members} lists={lists} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />}
    </div>
  );
}

function ShootForm({ shoot, members, lists, onClose, onSaved }) {
  const [f, setF] = useState({
    shoot_date: shoot.shoot_date || "", brand_project: shoot.brand_project || "", studio_location: shoot.studio_location || "",
    type: shoot.type || "", producer_id: shoot.producer_id || "", director_id: shoot.director_id || "", dop_id: shoot.dop_id || "",
    checklist: { ...(shoot.checklist || {}) }, budget: shoot.budget ?? "",
    planned_call: shoot.planned_call || "", planned_wrap: shoot.planned_wrap || "", actual_call: shoot.actual_call || "", actual_wrap: shoot.actual_wrap || "",
    planned_delivery: shoot.planned_delivery || "", actual_delivery: shoot.actual_delivery || "", shoot_status: shoot.shoot_status || "Planned",
    handover_date: shoot.handover_date || "", blockers_notes: shoot.blockers_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const editing = !!shoot.id;
  const done = CHECKLIST.filter(([k]) => f.checklist[k]).length;
  const memberOpts = members.map((m) => ({ value: m.id, label: m.name }));
  const save = () => {
    if (!f.shoot_date || !f.brand_project || !f.studio_location) return alert("Shoot date, brand/project and studio are required.");
    setSaving(true);
    const body = { ...f, budget: f.budget === "" ? null : Number(f.budget) };
    const req = editing ? api.put(`/ptracker/shoots/${shoot.id}`, body) : api.post("/ptracker/shoots", body);
    req.then(onSaved).catch((e) => { alert(e.response?.data?.detail || "Save failed"); setSaving(false); });
  };
  const del = () => { if (window.confirm("Delete this shoot?")) api.delete(`/ptracker/shoots/${shoot.id}`).then(onSaved).catch((e) => alert(e.response?.data?.detail || "Failed")); };
  const secLabel = { fontFamily: mono, fontSize: 10, fontWeight: 700, color: "var(--t3)", letterSpacing: ".1em", textTransform: "uppercase", marginTop: 6, marginBottom: 2 };

  return (
    <Drawer title={editing ? `Shoot — ${shoot.brand_project}` : "New shoot"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={secLabel}>Basics</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Shoot date"><Input type="date" value={f.shoot_date} onChange={(e) => set("shoot_date", e.target.value)} /></Field>
          <Field label="Brand / Project"><Select options={lists.brands} value={f.brand_project} onChange={(e) => set("brand_project", e.target.value)} allowEmpty /></Field>
          <Field label="Studio"><Select options={lists.studios} value={f.studio_location} onChange={(e) => set("studio_location", e.target.value)} allowEmpty /></Field>
          <Field label="Type"><Select options={TYPES} value={f.type} onChange={(e) => set("type", e.target.value)} allowEmpty /></Field>
          <Field label="Status"><Select options={STATUSES} value={f.shoot_status} onChange={(e) => set("shoot_status", e.target.value)} /></Field>
          <Field label="Budget (₹)"><Input type="number" value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder="Optional" /></Field>
        </div>

        <div style={secLabel}>Crew</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <Field label="Producer"><Select options={memberOpts} value={f.producer_id} onChange={(e) => set("producer_id", e.target.value)} allowEmpty /></Field>
          <Field label="Director"><Select options={memberOpts} value={f.director_id} onChange={(e) => set("director_id", e.target.value)} allowEmpty /></Field>
          <Field label="DOP"><Select options={memberOpts} value={f.dop_id} onChange={(e) => set("dop_id", e.target.value)} allowEmpty /></Field>
        </div>

        <div style={secLabel}>Readiness checklist ({done}/8)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {CHECKLIST.map(([k, lbl]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "var(--font-sans)", fontSize: 13, color: "var(--t1)", cursor: "pointer" }}>
              <input type="checkbox" checked={!!f.checklist[k]} onChange={(e) => set("checklist", { ...f.checklist, [k]: e.target.checked })} />{lbl}
            </label>
          ))}
        </div>

        <div style={secLabel}>Times</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Planned call"><Input type="time" value={f.planned_call} onChange={(e) => set("planned_call", e.target.value)} /></Field>
          <Field label="Planned wrap"><Input type="time" value={f.planned_wrap} onChange={(e) => set("planned_wrap", e.target.value)} /></Field>
          <Field label="Actual call"><Input type="time" value={f.actual_call} onChange={(e) => set("actual_call", e.target.value)} /></Field>
          <Field label="Actual wrap"><Input type="time" value={f.actual_wrap} onChange={(e) => set("actual_wrap", e.target.value)} /></Field>
        </div>

        <div style={secLabel}>Delivery & handover</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Planned delivery"><Input type="date" value={f.planned_delivery} onChange={(e) => set("planned_delivery", e.target.value)} /></Field>
          <Field label="Actual delivery"><Input type="date" value={f.actual_delivery} onChange={(e) => set("actual_delivery", e.target.value)} /></Field>
          <Field label="Handover date"><Input type="date" value={f.handover_date} onChange={(e) => set("handover_date", e.target.value)} /></Field>
        </div>

        <div style={secLabel}>Blockers / notes</div>
        <Textarea rows={2} value={f.blockers_notes} onChange={(e) => set("blockers_notes", e.target.value)} />

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          <div>{editing && <Btn kind="danger" onClick={del}>Delete</Btn>}</div>
          <div style={{ display: "flex", gap: 8 }}><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Btn></div>
        </div>
      </div>
    </Drawer>
  );
}
